import { YesObject } from '@yes.technology/react-toolkit'
import isEqual from 'lodash/isEqual'
import uniqBy from 'lodash/uniqBy'
import { Dispatch, SetStateAction, useEffect, useRef } from 'react'
import type { FullSelectionOption } from 'site/Renderer/components/SelectAndLink/SelectAndLink.types'
import { relationObjectsAtColumn } from '../../lib/relationObjectsAtColumn'
import { relationColumnUuids } from './relationColumnUuids'

type Params = {
  isUniselectionRelation: boolean
  currentField: string
  selectAndLinkSelectedOptions: FullSelectionOption[] | undefined
  currentRelationFromParent: YesObject[]
  unsavedObjects: YesObject[]
  setUnsavedObjects: Dispatch<SetStateAction<YesObject[]>>
  setSavedObjectsToRemove: Dispatch<SetStateAction<YesObject[]>>
  serverFieldValues: YesObject | undefined
  yesObjects: YesObject[]
  onChange: (fieldColumn: string, value: YesObject[string]) => void
  isFetching: boolean
  onScrollToRelationTable: () => void
}

const useRelationClassSelectAndLinkFieldSync = ({
  isUniselectionRelation,
  currentField,
  selectAndLinkSelectedOptions,
  currentRelationFromParent,
  unsavedObjects,
  setUnsavedObjects,
  setSavedObjectsToRemove,
  serverFieldValues,
  yesObjects,
  onChange,
  isFetching,
  onScrollToRelationTable
}: Params) => {
  const hadPersistentSelectionRef = useRef(false)
  const lastSelectAndLinkMergeUuidsRef = useRef<string[] | null>(null)

  useEffect(() => {
    lastSelectAndLinkMergeUuidsRef.current = null
  }, [currentField])

  useEffect(() => {
    if (selectAndLinkSelectedOptions === undefined) {
      lastSelectAndLinkMergeUuidsRef.current = null
      if (hadPersistentSelectionRef.current) {
        hadPersistentSelectionRef.current = false
        setUnsavedObjects((prev) => (prev.length ? [] : prev))
        setSavedObjectsToRemove([])
        const revert =
          serverFieldValues !== undefined
            ? relationObjectsAtColumn(serverFieldValues, currentField)
            : yesObjects
        onChange(currentField, uniqBy(revert, 'uuid'))
      }
      return
    }

    const normalizedSelectedOptions = isUniselectionRelation
      ? selectAndLinkSelectedOptions.slice(-1)
      : selectAndLinkSelectedOptions

    if (normalizedSelectedOptions.length === 0) {
      const serverRelation = relationObjectsAtColumn(
        serverFieldValues,
        currentField
      )
      const serverUuidSet = new Set(serverRelation.map((o) => o.uuid))
      const nextUnsaved = currentRelationFromParent.filter(
        (o) => !serverUuidSet.has(o.uuid)
      )
      setUnsavedObjects((prev) => {
        if (
          prev.length === nextUnsaved.length &&
          prev.every((p, i) => nextUnsaved[i]?.uuid === p.uuid)
        ) {
          return prev
        }
        return nextUnsaved
      })
      return
    }

    hadPersistentSelectionRef.current = true

    const savedUuids = new Set(currentRelationFromParent.map((o) => o.uuid))

    const objectsToLink = normalizedSelectedOptions.filter(
      (option) => !savedUuids.has(option.uuid)
    )

    if (isUniselectionRelation) {
      const nextRelation = normalizedSelectedOptions as YesObject[]
      const nextUuids = relationColumnUuids(nextRelation)
      const currentUuids = relationColumnUuids(currentRelationFromParent)

      if (isEqual(nextUuids, currentUuids)) {
        lastSelectAndLinkMergeUuidsRef.current = [...nextUuids]
      } else if (!isEqual(nextUuids, lastSelectAndLinkMergeUuidsRef.current)) {
        onChange(currentField, nextRelation)
        lastSelectAndLinkMergeUuidsRef.current = [...nextUuids]
      }
    } else {
      const relationBase = uniqBy(
        [...currentRelationFromParent, ...unsavedObjects],
        'uuid'
      )

      if (objectsToLink.length || unsavedObjects.length) {
        const nextRelation = uniqBy(
          [...relationBase, ...objectsToLink],
          'uuid'
        ) as YesObject[]
        const nextUuids = relationColumnUuids(nextRelation)
        const currentUuids = relationColumnUuids(currentRelationFromParent)

        if (isEqual(nextUuids, currentUuids)) {
          lastSelectAndLinkMergeUuidsRef.current = [...nextUuids]
        } else if (
          !isEqual(nextUuids, lastSelectAndLinkMergeUuidsRef.current)
        ) {
          onChange(currentField, nextRelation)
          lastSelectAndLinkMergeUuidsRef.current = [...nextUuids]
        }
      }
    }

    const yesObjectUuids = new Set(yesObjects.map((o) => o.uuid))
    const nextUnsaved = normalizedSelectedOptions.filter(
      (option) => !yesObjectUuids.has(option.uuid)
    ) as YesObject[]

    setUnsavedObjects((prev) => {
      if (
        prev.length === nextUnsaved.length &&
        prev.every((p, i) => nextUnsaved[i]?.uuid === p.uuid)
      ) {
        return prev
      }
      return nextUnsaved
    })
  }, [
    isUniselectionRelation,
    currentField,
    selectAndLinkSelectedOptions,
    onChange,
    currentRelationFromParent,
    unsavedObjects,
    serverFieldValues,
    yesObjects,
    setUnsavedObjects,
    setSavedObjectsToRemove
  ])

  useEffect(() => {
    if (isFetching || !unsavedObjects.length) return

    onScrollToRelationTable()
  }, [unsavedObjects, isFetching, onScrollToRelationTable])
}

export default useRelationClassSelectAndLinkFieldSync
